import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import { fmtInt, fmtPct, fmtUsd } from 'components/upb_match_explorer/format';
import FsaLoader from 'components/shared/FsaLoader';
import InfluenceStatCard from 'components/influence_sku_mapping/InfluenceStatCard';
import { PRIMARY_COLOR } from 'components/upb_viewer_beta/css/betaTokens';

// Selected product campaign status -> the adjective used in the cohort-card wording.
const STATUS_WORD = { ACTIVE: 'active', DRAFT: 'draft' };

/**
 * InfluenceOverviewSummary — cohort-wide headline stat cards across all influence-enabled
 * manufacturers: total campaign MPNs, mapped distributor SKUs %, and mapped GMV %.
 */
function InfluenceOverviewSummary({ totals, loading, campaignStatus }) {
  if (loading) {
    return (
      <div className={css(styles.loading)}>
        <FsaLoader />
      </div>
    );
  }
  const t = totals || {};
  const campaignRatio = t.active_catalog_skus ? (t.campaign_skus / t.active_catalog_skus) * 100 : 0;

  // When a status is selected the cohort cards describe that status specifically; otherwise keep
  // the generic cohort wording.
  const statusWord = STATUS_WORD[campaignStatus] || '';
  const campaignCaption = statusWord
    ? `Enrolled in ${statusWord === 'active' ? 'an' : 'a'} ${statusWord} campaign`
    : 'Enrolled in an active or draft campaign';
  const tam = statusWord ? `${statusWord} TAM` : 'TAM';

  const cards = [
    <InfluenceStatCard
      key="campaign"
      accent="primary"
      label="Campaign MPNs"
      value={fmtInt(t.campaign_skus)}
      caption={campaignCaption}
      ratio={campaignRatio}
      footer={`of ${fmtInt(t.active_catalog_skus)} active on C&D`}
    />,
    <InfluenceStatCard
      key="mapped-skus"
      accent="success"
      label="Mapped SKUs"
      value={fmtPct(t.mapped_pct)}
      caption={`DP SKUs mapped to the mfr catalog · of ${tam}`}
      ratio={t.mapped_pct}
      footer={`${fmtInt(t.mapped)} / ${fmtInt(t.tam)} SKUs`}
    />,
    <InfluenceStatCard
      key="mapped-gmv"
      accent="info"
      label="Mapped GMV"
      value={fmtPct(t.gmv_pct)}
      caption={`DP GMV mapped to the mfr catalog · of ${tam} GMV`}
      ratio={t.gmv_pct}
      footer={`${fmtUsd(t.mapped_gmv)} / ${fmtUsd(t.tam_gmv)}`}
    />,
    <InfluenceStatCard
      key="influence-dps"
      accent="warning"
      label="Influence DPs"
      value={`${fmtInt(t.dp_count)} / ${fmtInt(t.total_influence_dps)}`}
      caption={`Distinct distributors carrying ${tam} SKUs · of all influence DPs`}
      ratio={t.total_influence_dps ? (t.dp_count / t.total_influence_dps) * 100 : 0}
    />,
    <InfluenceStatCard
      key="ez-pass"
      accent="primary"
      label="EZ Pass Mapped"
      value={fmtInt(t.ez_pass_skus)}
      caption="Campaign-MPN-mapped DP SKUs mapped via the Brand SKU Mapper (EZ-pass) tools"
      footer={`${fmtUsd(t.ez_pass_gmv)} GMV`}
    />,
  ];
  // Status-scoped reconciliation cards -- present only when a campaign product status is selected
  // (the backend returns these fields only then).
  if (t.outside_campaign_skus != null) {
    cards.push(
      <InfluenceStatCard
        key="avg-maps"
        accent="primary"
        label="Avg MPN-SKU Maps"
        value={Number(t.avg_mpn_sku_maps || 0).toFixed(1)}
        caption="Avg influence DP SKUs mapped per campaign MPN"
        footer={`${fmtInt(t.mpn_sku_influence_skus)} SKUs / ${fmtInt(t.campaign_skus)} MPNs`}
      />,
      <InfluenceStatCard
        key="mpn-sku-influence"
        accent="success"
        label="MPN-SKU Influence SKUs"
        value={fmtInt(t.mpn_sku_influence_skus)}
        caption="Influence DP SKUs mapped to a campaign MPN (incl. off-brand matches)"
        footer={`${fmtUsd(t.mpn_sku_influence_gmv)} GMV`}
      />,
      <InfluenceStatCard
        key="outside-campaign"
        accent="warning"
        label="Outside Campaign SKUs"
        value={fmtInt(t.outside_campaign_skus)}
        caption="Influence DP SKUs mapped to active, non-campaign catalog products"
        footer={`${fmtUsd(t.outside_campaign_gmv)} GMV`}
      />,
      <InfluenceStatCard
        key="off-brand"
        accent="info"
        label="Off-Brand Matches"
        value={fmtInt(t.off_brand_matches)}
        caption="Mapped SKUs whose brand isn't a campaign brand"
        footer={`${fmtUsd(t.off_brand_matches_gmv)} GMV`}
      />,
    );
  }

  // Up to 5 cards per row; a partial row stretches its cards to fill the full width (flex-grow
  // against a 20% basis). Each card is boxed in a cell so the cap holds regardless of card count.
  return (
    <div className={css(styles.grid)}>
      {cards.map(card => (
        <div key={card.key} className={css(styles.cell)}>
          {card}
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  // Flex-wrap row of cells, max 5 per row; a partial row stretches its cards to fill the width.
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 8,
  },
  // 20% basis (minus its share of the 4 inter-card gaps) caps the row at 5; flex-grow lets a
  // shorter row expand to full width. Drops to 2-up, then 1-up, on narrower screens.
  cell: {
    display: 'flex',
    flex: '1 1 calc(20% - 12.8px)',
    minWidth: 220,
    '@media (max-width: 1200px)': { flexBasis: 'calc(50% - 8px)' },
    '@media (max-width: 680px)': { flexBasis: '100%' },
  },
  loading: { display: 'flex', justifyContent: 'center', padding: 40, color: PRIMARY_COLOR },
});

InfluenceOverviewSummary.propTypes = {
  totals: PropTypes.shape({
    campaign_skus: PropTypes.number,
    active_catalog_skus: PropTypes.number,
    mapped: PropTypes.number,
    tam: PropTypes.number,
    mapped_pct: PropTypes.number,
    mapped_gmv: PropTypes.number,
    tam_gmv: PropTypes.number,
    gmv_pct: PropTypes.number,
    dp_count: PropTypes.number,
    total_influence_dps: PropTypes.number,
    ez_pass_skus: PropTypes.number,
    ez_pass_gmv: PropTypes.number,
    avg_mpn_sku_maps: PropTypes.number,
    mpn_sku_influence_skus: PropTypes.number,
    mpn_sku_influence_gmv: PropTypes.number,
    outside_campaign_skus: PropTypes.number,
    outside_campaign_gmv: PropTypes.number,
    off_brand_matches: PropTypes.number,
    off_brand_matches_gmv: PropTypes.number,
  }),
  loading: PropTypes.bool,
  campaignStatus: PropTypes.string,
};

export default InfluenceOverviewSummary;
